// src/AuthContext.js
import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { supabase } from './supabaseClient';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);

  // ── Mise à jour last_seen ─────────────────────────────────────────────────
  const updateLastSeen = async (userId) => {
    if (!userId) return;
    await supabase.from('profiles')
      .update({ last_seen: new Date().toISOString() })
      .eq('id', userId);
  };

  // ── Démarrer le ping toutes les 2 minutes ─────────────────────────────────
  const startPing = (userId) => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    updateLastSeen(userId); // ping immédiat
    intervalRef.current = setInterval(() => updateLastSeen(userId), 2 * 60 * 1000);
  };

  const stopPing = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    // Récupérer la session active au démarrage
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
        startPing(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Écouter les changements d'authentification
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (_event === 'PASSWORD_RECOVERY') {
          sessionStorage.setItem('sqp_reset_mode', '1');
        }
        setUser(session?.user ?? null);
        if (session?.user) {
          fetchProfile(session.user.id);
          startPing(session.user.id);
        } else {
          setProfile(null);
          setLoading(false);
          stopPing();
        }
      }
    );

    return () => {
      subscription.unsubscribe();
      stopPing();
    };
  }, []);

  const fetchProfile = async (userId) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    if (!error) setProfile(data);
    setLoading(false);
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    return { data, error };
  };

  const signOut = async () => {
    stopPing();
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
  };

  return (
    <AuthContext.Provider value={{ user, profile, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
